import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import {
    createAssignment,
    deleteAssignment,
    getMyAssignments,
    updateAssignment,
} from "../../redux/teacherRelated/teacherToolsHandle";
import { underToolControl } from "../../redux/teacherRelated/teacherToolsSlice";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../hooks/useTermRecordsScope";

const formatDate = (d) => new Date(d).toLocaleDateString("en-GB");

const TYPE_OPTIONS = ["Homework", "Class Assignment", "CA Test", "Project", "Practical"];
const TERM_FILTER_OPTIONS = ["All", "Term 1", "Term 2", "Term 3"];
const TERM_FORM_OPTIONS = ["Term 1", "Term 2", "Term 3", "Semester 1", "Semester 2"];

const TYPE_CHIP = {
    Homework: "default",
    "Class Assignment": "info",
    "CA Test": "warning",
    Project: "secondary",
    Practical: "success",
};

function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function daysLeftLabel(dueDate) {
    if (!dueDate) return { text: "—", color: "text.secondary" };
    const today = startOfDay(new Date());
    const due = startOfDay(dueDate);
    const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));

    if (diff > 3) {
        return { text: `${diff} days left`, color: "success.main" };
    }
    if (diff >= 1) {
        return { text: `${diff} days left`, color: "warning.main" };
    }
    if (diff === 0) {
        return { text: "Due today", color: "error.main" };
    }
    return { text: `Overdue by ${Math.abs(diff)} days`, color: "error.main" };
}

function todayInputValue() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function toDateInputValue(value) {
    if (!value) return todayInputValue();
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return todayInputValue();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

const emptyForm = () => ({
    title: "",
    assignmentType: "Homework",
    description: "",
    dueDate: todayInputValue(),
    maxMarks: 20,
    term: "Term 1",
    academicYear: String(new Date().getFullYear()),
});

const TeacherAssignments = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { assignmentsList, loading, statestatus, response, error } = useSelector((s) => s.teacherTools);

    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.schoolId;
    const { activeSubjectId: subjectId, activeClassId: classId, subjectName, className } = useTeacherTeaching();
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [typeFilter, setTypeFilter] = useState("All");
    const [termFilter, setTermFilter] = useState("All");
    const [appliedType, setAppliedType] = useState("All");
    const [appliedTerm, setAppliedTerm] = useState("All");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const allAssignments = useMemo(
        () => (Array.isArray(assignmentsList) ? assignmentsList : []),
        [assignmentsList]
    );

    const assignments = useMemo(() => {
        return allAssignments.filter((row) => {
            if (appliedType !== "All" && row.assignmentType !== appliedType) return false;
            if (appliedTerm !== "All" && row.term !== appliedTerm) return false;
            return true;
        });
    }, [allAssignments, appliedType, appliedTerm]);

    const reloadAssignments = useCallback(
        (term) => {
            if (!teacherId) return;
            const filters = { recordsScope };
            if (term && term !== "All") filters.term = term;
            dispatch(getMyAssignments(teacherId, filters));
        },
        [dispatch, teacherId, recordsScope]
    );

    useEffect(() => {
        reloadAssignments();
    }, [reloadAssignments, recordsScope]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Completed successfully!");
            setShowPopup(true);
            reloadAssignments(appliedTerm);
            setDialogOpen(false);
            setEditingId(null);
            setForm(emptyForm());
            dispatch(underToolControl());
        }
    }, [statestatus, dispatch, reloadAssignments, appliedTerm]);

    useEffect(() => {
        const msg = response || error;
        if (msg && statestatus !== "added") {
            setMessage(typeof msg === "string" ? msg : "Request failed");
            setShowPopup(true);
            dispatch(underToolControl());
        }
    }, [response, error, statestatus, dispatch]);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleApplyFilter = () => {
        setAppliedType(typeFilter);
        setAppliedTerm(termFilter);
        reloadAssignments(termFilter);
    };

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditingId(row.id || row._id);
        setForm({
            title: row.title || "",
            assignmentType: row.assignmentType || "Homework",
            description: row.description || "",
            dueDate: toDateInputValue(row.dueDate),
            maxMarks: Number(row.maxMarks) || 20,
            term: row.term || "Term 1",
            academicYear: row.academicYear || String(new Date().getFullYear()),
        });
        setDialogOpen(true);
    };

    const buildPayload = () => ({
        teacherId,
        schoolId,
        subjectId,
        classId,
        title: String(form.title).trim(),
        assignmentType: form.assignmentType,
        description: String(form.description || "").trim(),
        dueDate: form.dueDate,
        maxMarks: Number(form.maxMarks) > 0 ? Math.round(Number(form.maxMarks)) : 20,
        term: form.term,
        academicYear: String(form.academicYear || "").trim(),
    });

    const handleSubmit = () => {
        if (!teacherId || !schoolId || !subjectId || !classId) {
            setMessage("Missing teacher, school, subject or class information");
            setShowPopup(true);
            return;
        }
        if (!String(form.title || "").trim()) {
            setMessage("Title is required");
            setShowPopup(true);
            return;
        }
        if (!form.dueDate) {
            setMessage("Due date is required");
            setShowPopup(true);
            return;
        }

        const payload = buildPayload();
        if (editingId) {
            const { teacherId: _t, schoolId: _s, subjectId: _sub, classId: _c, ...updateBody } = payload;
            dispatch(updateAssignment(editingId, updateBody));
        } else {
            dispatch(createAssignment(payload));
        }
    };

    const handleDelete = (id) => {
        if (window.confirm("Delete this assignment?")) {
            dispatch(deleteAssignment(id));
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Assignments & Homework
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
                        Set homework, CA tests, and class assignments for your students.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0 }}>
                    Create Assignment
                </Button>
            </Stack>

            <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
            <TermRecordsScopeAlert
                scope={recordsScope}
                currentTermLabel={currentTermLabel}
                sectionLabel="assignments"
            />

            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Assignment type</InputLabel>
                        <Select label="Assignment type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                            <MenuItem value="All">All</MenuItem>
                            {TYPE_OPTIONS.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Term</InputLabel>
                        <Select label="Term" value={termFilter} onChange={(e) => setTermFilter(e.target.value)}>
                            {TERM_FILTER_OPTIONS.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={handleApplyFilter}>
                        Apply
                    </Button>
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer sx={{ overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Title</StyledTableCell>
                                <StyledTableCell>Type</StyledTableCell>
                                <StyledTableCell>Due date</StyledTableCell>
                                <StyledTableCell>Max marks</StyledTableCell>
                                <StyledTableCell>Days left</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading && assignments.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6}>
                                        <Skeleton height={48} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : assignments.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No assignments match your filters.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                assignments.map((row) => {
                                    const id = row.id || row._id;
                                    const type = row.assignmentType || "Homework";
                                    const left = daysLeftLabel(row.dueDate);
                                    return (
                                        <StyledTableRow key={id} hover>
                                            <StyledTableCell>{row.title}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    label={type}
                                                    size="small"
                                                    color={TYPE_CHIP[type] || "default"}
                                                    variant={type === "Homework" ? "outlined" : "filled"}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>{formatDate(row.dueDate)}</StyledTableCell>
                                            <StyledTableCell>{Number(row.maxMarks) || 20}</StyledTableCell>
                                            <StyledTableCell>
                                                <Typography variant="body2" sx={{ color: left.color, fontWeight: 600 }}>
                                                    {left.text}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDelete(id)}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? "Edit assignment" : "Create assignment"}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        <strong>Subject:</strong> {subjectName} · <strong>Class:</strong> {className}
                    </Typography>

                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            required
                            size="small"
                            label="Title"
                            value={form.title}
                            onChange={(e) => setField("title", e.target.value)}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Assignment type</InputLabel>
                            <Select
                                label="Assignment type"
                                value={form.assignmentType}
                                onChange={(e) => setField("assignmentType", e.target.value)}
                            >
                                {TYPE_OPTIONS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            size="small"
                            label="Description"
                            value={form.description}
                            onChange={(e) => setField("description", e.target.value)}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    size="small"
                                    type="date"
                                    label="Due date"
                                    value={form.dueDate}
                                    onChange={(e) => setField("dueDate", e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Max marks"
                                    value={form.maxMarks}
                                    onChange={(e) => setField("maxMarks", e.target.value)}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Term</InputLabel>
                                    <Select label="Term" value={form.term} onChange={(e) => setField("term", e.target.value)}>
                                        {TERM_FORM_OPTIONS.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {t}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Academic year"
                                    value={form.academicYear}
                                    onChange={(e) => setField("academicYear", e.target.value)}
                                    placeholder="2025"
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                        {editingId ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Container>
    );
};

export default TeacherAssignments;
